import { type ReactNode } from 'react';

import { type GuideGridConfig } from '~/components/EditMode/components/BoundingBoxLayerKonva/components/shapes/GuideGrid';
import { type PositionedImage } from '~/components/EditMode/components/BoundingBoxLayerKonva/services/PositionedImage';
import {
  type PercentagePoint,
  type PercentageRect,
  type PixelRect,
} from '~/components/EditMode/components/BoundingBoxLayerKonva/types';

export enum SelectionMode {
  INSIDE,
  OUTSIDE,
  FREE,
}

export enum MoveMode {
  BOX,
  IMAGE,
}

export enum RefocusMode {
  NONE,
  BOX,
}

export type BoundingBoxLayerMouseEventHandler = (params: {
  point: PercentagePoint;
  event: globalThis.MouseEvent;
  isHoveringBox: boolean;
}) => boolean;

export type BoundingBoxLayerKonvaProps<T extends PercentageRect> = {
  shouldDrawOverlay: boolean;
  disabled?: boolean;
  onIsSelectingChange?: (isSelecting: boolean) => void;
  containerWidth: number;
  containerHeight: number;
  containerLeft: number;
  containerTop: number;
  stageWidth: number;
  stageHeight: number;
  stageLeft: number;
  stageTop: number;
  onClearAllRequested?: () => void;
  onClear?: () => void;
  onBoundingBoxDrawn?: (box: PercentageRect) => void;
  boundingBoxes: T[];
  hasCustomCursor?: boolean;
  editableBoundingBoxIndex?: number;
  drawNewBox?: (params: { rect: PercentageRect }) => ReactNode;
  /** MouseDown event handler. Return true if event was handled and shouldn't be processed in this component */
  onMouseDown?: BoundingBoxLayerMouseEventHandler;
  /** MouseUp event handler. Return true if event was handled and shouldn't be processed in this component */
  onMouseUp?: BoundingBoxLayerMouseEventHandler;
  overlayColor?: string;
  drawBoxes: (params: { boxes: PercentageRect[] }) => ReactNode;
  onBoxHovered?: (box: T | undefined) => void;
  /** Show guide grid on editable bounding box (default: false) */
  showGuideGrid?: boolean;
  /** Guide grid configuration */
  guideGridConfig?: GuideGridConfig;
  /** Enable debug mode to output console.debug statements about bounding box rect (default: false) */
  debug?: boolean;
  /** Optional aspect ratio constraint (width / height) for drawing and resizing */
  aspectRatio?: number;
  preserveAspectRatioBox: boolean;
  selectionMode: SelectionMode;
  moveMode: MoveMode;
  refocusMode: RefocusMode;
  positionedImage: PositionedImage;
};

export type BoundingBoxHandlesProps = {
  rect: PixelRect;
  strokeColor: string;
  strokeWidth?: number;
};
